import { useState, useCallback, useMemo, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  // Tabs,
  // Tab,
  IconButton,
  TextField,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useTheme } from '@/contexts/ThemeContext'
import { useTreasuryBalance } from '@/contexts/TreasuryBalanceContext'
import {
  useWaitForTransactionReceipt,
  usePublicClient,
  useChainId,
} from 'wagmi'
import { getEnv } from '@/config/env'
import STNGImplementationAbi from '@/abis/STNGImplementation.json'
import { useNotification } from '@/hooks/useNotification'
import { parseUnits } from 'viem'
import type { Abi } from 'viem'
import { isUserRejectedError } from '@/utils/errorHandler'
import TransactionCancelledModal from '../TransactionCancelledModal'
import useSafe from '@/localsafe.eth/hooks/useSafe'
import { submitSafeTransactionToService } from '@/utils/safeTransactionService'
import { DEFAULT_TOKEN_DECIMALS } from '@/constants/main'
import { formatAddress, formatHash } from '@/utils/formatting'
import {
  buildSafeTransactionParams,
  encodeContractCall,
} from '@/utils/safeTransactionHelpers'
import {
  createTextFieldStyles,
  createTextFieldErrorStyles,
} from '@/components/shared/commonStyles'
import { useWallet } from '@/hooks/useWallet'

interface TransferModalProps {
  open: boolean
  onClose: () => void
  safeAddress: string
  safeName: string
}

// interface TabPanelProps {
//   children?: React.ReactNode
//   index: number
//   value: number
// }

// function TabPanel(props: TabPanelProps) {
//   const { children, value, index, ...other } = props

//   return (
//     <div
//       role="tabpanel"
//       hidden={value !== index}
//       id={`transfer-tabpanel-${index}`}
//       aria-labelledby={`transfer-tab-${index}`}
//       {...other}
//     >
//       {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
//     </div>
//   )
// }

const ERC20_TRANSFER_ABI = [
  {
    inputs: [
      { internalType: 'address', name: 'to', type: 'address' },
      { internalType: 'uint256', name: 'amount', type: 'uint256' },
    ],
    name: 'transfer',
    outputs: [{ internalType: 'bool', name: '', type: 'bool' }],
    stateMutability: 'nonpayable',
    type: 'function',
  },
] as const

export default function TransferModal({
  open,
  onClose,
  safeAddress,
  safeName,
}: TransferModalProps) {
  const [tabValue, setTabValue] = useState(0)
  const [toAmount, setToAmount] = useState('')
  const [toAmountError, setToAmountError] = useState('')
  const [fromAmount, setFromAmount] = useState('')
  const [decimals, setDecimals] = useState<number>(DEFAULT_TOKEN_DECIMALS)
  const [isTransferring, setIsTransferring] = useState(false)
  const { colors } = useTheme()
  const { treasuryBalance } = useTreasuryBalance()
  const [showCancelledModal, setShowCancelledModal] = useState(false)
  const [cancelledMessage, setCancelledMessage] = useState<string>('')
  const [txHash, setTxHash] = useState<`0x${string}` | null>(null)
  const { showSuccess, showError } = useNotification()
  const { treasurySafe, stngProxy, operationalSafeFactory } = useMemo(
    () => getEnv(),
    []
  )
  const publicClient = usePublicClient()
  const chainId = useChainId()
  const { address: userAddress } = useWallet()

  const {
    isReady: isTreasurySafeReady,
    buildSafeTransaction: buildTreasurySafeTransaction,
    signSafeTransaction: signTreasurySafeTransaction,
    executeSafeTransaction: executeTreasurySafeTransaction,
    getSafeTransactionHash: getTreasurySafeTransactionHash,
    isTransactionReadyToExecute: isTreasuryTransactionReadyToExecute,
  } = useSafe(treasurySafe as `0x${string}`)

  const {
    isReady: isOperationalSafeReady,
    buildSafeTransaction: buildOperationalSafeTransaction,
    signSafeTransaction: signOperationalSafeTransaction,
    executeSafeTransaction: executeOperationalSafeTransaction,
    getSafeTransactionHash: getOperationalSafeTransactionHash,
    isTransactionReadyToExecute: isOperationalTransactionReadyToExecute,
  } = useSafe(safeAddress as `0x${string}`)

  // const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
  //   setTabValue(newValue)
  // }

  useEffect(() => {
    const fetchData = async () => {
      if (!publicClient || !stngProxy || !operationalSafeFactory) return
      try {
        const contractDecimals = (await publicClient.readContract({
          address: stngProxy as `0x${string}`,
          abi: STNGImplementationAbi as Abi,
          functionName: 'decimals',
        })) as number
        setDecimals(contractDecimals)
      } catch (error) {
        console.error('Failed to fetch decimals:', error)
      }
    }
    if (open) {
      fetchData()
      setToAmount('')
      setToAmountError('')
      setFromAmount('')
    }
  }, [publicClient, stngProxy, operationalSafeFactory, open])

  const {
    isLoading: isConfirmingTx,
    isSuccess: isTxSuccess,
    error: txError,
  } = useWaitForTransactionReceipt({
    hash: txHash || undefined,
  })

  useEffect(() => {
    if (isTxSuccess && isTransferring && txHash) {
      setIsTransferring(false)
      setTxHash(null)
      if (tabValue === 0) {
        setToAmount('')
        setToAmountError('')
        showSuccess('Tokens transferred successfully!')
      } else {
        setFromAmount('')
        showSuccess('Tokens transferred successfully!')
      }
      onClose()
    }
  }, [isTxSuccess, isTransferring, txHash, tabValue, showSuccess, onClose])

  useEffect(() => {
    if (txError && isTransferring && txHash) {
      setIsTransferring(false)
      setTxHash(null)
      if (isUserRejectedError(txError)) {
        setCancelledMessage(
          'You cancelled the transfer transaction in MetaMask.'
        )
        setShowCancelledModal(true)
      } else {
        showError(
          txError.message || 'Failed to transfer tokens. Please try again.'
        )
      }
    }
  }, [txError, isTransferring, txHash, tabValue, showError])

  const handleClose = () => {
    setTabValue(0)
    setToAmount('')
    setToAmountError('')
    setFromAmount('')
    setIsTransferring(false)
    onClose()
  }

  const handleToAmountChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.replace(/[^0-9.]/g, '')
      setToAmount(value)

      if (value && treasuryBalance !== null) {
        try {
          const amountInWei = parseUnits(value, decimals)
          if (amountInWei > treasuryBalance) {
            setToAmountError('Amount exceeds treasury balance')
          } else {
            setToAmountError('')
          }
        } catch {
          setToAmountError('')
        }
      } else {
        setToAmountError('')
      }
    },
    [decimals, treasuryBalance]
  )

  // const handleFromAmountChange = useCallback(
  //   (e: React.ChangeEvent<HTMLInputElement>) => {
  //     const value = e.target.value.replace(/[^0-9.]/g, '')
  //     setFromAmount(value)
  //   },
  //   []
  // )

  const handleAmountKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === '-' || e.key === 'e' || e.key === 'E' || e.key === '+') {
        e.preventDefault()
      }
    },
    []
  )

  const handleTransfer = useCallback(async () => {
    try {
      setIsTransferring(true)

      if (tabValue === 0) {
        if (!isTreasurySafeReady) {
          showError('Treasury Safe is not ready. Please wait...')
          setIsTransferring(false)
          return
        }
        if (!toAmount || parseFloat(toAmount) <= 0) {
          showError('Please enter a valid amount')
          setIsTransferring(false)
          return
        }

        if (treasuryBalance === null) {
          showError('Unable to fetch treasury balance. Please try again.')
          setIsTransferring(false)
          return
        }

        const amountInWei = parseUnits(toAmount, decimals)
        if (amountInWei > treasuryBalance) {
          showError('Amount exceeds treasury balance')
          setIsTransferring(false)
          return
        }

        const data = encodeContractCall(ERC20_TRANSFER_ABI, 'transfer', [
          safeAddress as `0x${string}`,
          amountInWei,
        ])

        const safeTx = await buildTreasurySafeTransaction([
          buildSafeTransactionParams({
            to: stngProxy as `0x${string}`,
            data,
          }),
        ])

        if (!safeTx) {
          showError('Failed to build Safe transaction')
          setIsTransferring(false)
          return
        }

        let signedTx
        try {
          signedTx = await signTreasurySafeTransaction(safeTx)

          const txNonce = signedTx.data.nonce
          if (typeof window !== 'undefined') {
            window.dispatchEvent(
              new CustomEvent('transaction-creating', {
                detail: { nonce: txNonce, type: 'Transfer' },
              })
            )
          }

          if (chainId && userAddress) {
            try {
              const submitResult = await submitSafeTransactionToService(
                signedTx,
                treasurySafe,
                chainId,
                userAddress,
                getTreasurySafeTransactionHash
              )
              if (!submitResult.success) {
                if (typeof window !== 'undefined') {
                  window.dispatchEvent(
                    new CustomEvent('transaction-creation-failed', {
                      detail: { nonce: txNonce },
                    })
                  )
                }
                console.error(
                  'Failed to submit transaction to Safe Transaction Service:',
                  submitResult.error
                )
                showError(
                  `Transaction created but failed to sync with Safe Transaction Service: ${submitResult.error}`
                )
              }
            } catch (error) {
              if (typeof window !== 'undefined') {
                window.dispatchEvent(
                  new CustomEvent('transaction-creation-failed', {
                    detail: { nonce: txNonce },
                  })
                )
              }
              console.error(
                'Error submitting transaction to Safe Transaction Service:',
                error
              )
            }
          }
        } catch (error) {
          setIsTransferring(false)
          if (isUserRejectedError(error)) {
            setCancelledMessage(
              'You cancelled the signature request in MetaMask.'
            )
            setShowCancelledModal(true)
          } else {
            showError(
              error instanceof Error
                ? error.message
                : 'Failed to sign Safe transaction'
            )
          }
          return
        }

        const isReady = await isTreasuryTransactionReadyToExecute(signedTx)

        if (isReady) {
          try {
            const hash = await executeTreasurySafeTransaction(signedTx)
            if (hash) {
              setTxHash(hash)
            }
          } catch (error) {
            setIsTransferring(false)
            if (isUserRejectedError(error)) {
              setCancelledMessage(
                'You cancelled the execution transaction in MetaMask.'
              )
              setShowCancelledModal(true)
            } else {
              showError(
                error instanceof Error
                  ? error.message
                  : 'Failed to execute transfer transaction'
              )
            }
          }
        } else {
          setIsTransferring(false)
          if (tabValue === 0) {
            setToAmount('')
          } else {
            setFromAmount('')
          }
          getTreasurySafeTransactionHash(signedTx)
            .then((txHash) => {
              showSuccess(
                `Transfer transaction created and signed! It requires more signatures before execution. Transaction hash: ${formatHash(
                  txHash
                )}...`
              )
            })
            .catch(() => {
              showSuccess(
                'Transfer transaction created and signed! It requires more signatures before execution.'
              )
            })
          onClose()
        }
      } else {
        // Transfer From: transfer tokens from operationalSafe to treasurySafe
        // Uses direct transfer call on dKZT token contract, executed as a Safe transaction from operationalSafe
        if (!isOperationalSafeReady) {
          showError('Operational Safe is not ready. Please wait...')
          setIsTransferring(false)
          return
        }
        if (!fromAmount || parseFloat(fromAmount) <= 0) {
          showError('Please enter a valid amount')
          setIsTransferring(false)
          return
        }

        if (!stngProxy) {
          showError('dKZT token address is not configured')
          setIsTransferring(false)
          return
        }

        if (!treasurySafe) {
          showError('Treasury Safe address is not configured')
          setIsTransferring(false)
          return
        }

        const amountInWei = parseUnits(fromAmount, decimals)

        const data = encodeContractCall(ERC20_TRANSFER_ABI, 'transfer', [
          treasurySafe as `0x${string}`,
          amountInWei,
        ])

        const safeTx = await buildOperationalSafeTransaction([
          buildSafeTransactionParams({
            to: stngProxy as `0x${string}`,
            data,
          }),
        ])

        if (!safeTx) {
          showError('Failed to build Safe transaction')
          setIsTransferring(false)
          return
        }

        // Sign the transaction
        let signedTx
        try {
          signedTx = await signOperationalSafeTransaction(safeTx)

          if (chainId && userAddress) {
            try {
              const submitResult = await submitSafeTransactionToService(
                signedTx,
                safeAddress,
                chainId,
                userAddress,
                getOperationalSafeTransactionHash
              )
              if (!submitResult.success) {
                console.error(
                  'Failed to submit transaction to Safe Transaction Service:',
                  submitResult.error
                )
                showError(
                  `Transaction created but failed to sync with Safe Transaction Service: ${submitResult.error}`
                )
              }
            } catch (error) {
              console.error(
                'Error submitting transaction to Safe Transaction Service:',
                error
              )
            }
          }
        } catch (error) {
          setIsTransferring(false)
          if (isUserRejectedError(error)) {
            setCancelledMessage(
              'You cancelled the signature request in MetaMask.'
            )
            setShowCancelledModal(true)
          } else {
            showError(
              error instanceof Error
                ? error.message
                : 'Failed to sign Safe transaction'
            )
          }
          return
        }

        const isReady = await isOperationalTransactionReadyToExecute(signedTx)

        if (isReady) {
          try {
            const hash = await executeOperationalSafeTransaction(signedTx)
            if (hash) {
              setTxHash(hash)
            }
          } catch (error) {
            setIsTransferring(false)
            if (isUserRejectedError(error)) {
              setCancelledMessage(
                'You cancelled the execution transaction in MetaMask.'
              )
              setShowCancelledModal(true)
            } else {
              showError(
                error instanceof Error
                  ? error.message
                  : 'Failed to execute transfer transaction'
              )
            }
          }
        } else {
          setIsTransferring(false)
          if (tabValue === 0) {
            setToAmount('')
          } else {
            setFromAmount('')
          }
          getOperationalSafeTransactionHash(signedTx)
            .then((txHash) => {
              showSuccess(
                `Transfer transaction created and signed! It requires more signatures before execution. Transaction hash: ${formatHash(
                  txHash
                )}...`
              )
            })
            .catch(() => {
              showSuccess(
                'Transfer transaction created and signed! It requires more signatures before execution.'
              )
            })
          onClose()
        }
      }
    } catch (error) {
      setIsTransferring(false)
      showError(
        error instanceof Error
          ? error.message
          : 'Failed to execute transfer. Please try again.'
      )
    }
  }, [
    tabValue,
    toAmount,
    fromAmount,
    decimals,
    safeAddress,
    stngProxy,
    isTreasurySafeReady,
    isOperationalSafeReady,
    buildTreasurySafeTransaction,
    signTreasurySafeTransaction,
    executeTreasurySafeTransaction,
    getTreasurySafeTransactionHash,
    isTreasuryTransactionReadyToExecute,
    buildOperationalSafeTransaction,
    signOperationalSafeTransaction,
    executeOperationalSafeTransaction,
    getOperationalSafeTransactionHash,
    isOperationalTransactionReadyToExecute,
    chainId,
    userAddress,
    treasurySafe,
    treasuryBalance,
    showSuccess,
    showError,
    onClose,
  ])

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: colors.background,
          border: `1px solid ${colors.primary}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          color: colors.yellow,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontWeight: 600,
        }}
      >
        Transfer
        <IconButton onClick={handleClose} sx={{ color: colors.text }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {/* <Box sx={{ borderBottom: 1, borderColor: `${colors.primary}33` }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{
              '& .MuiTab-root': {
                color: colors.text,
                opacity: 0.7,
                '&.Mui-selected': {
                  color: colors.text,
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: colors.yellow,
              },
            }}
          >
            <Tab label="" />
            <Tab label="Transfer To" />
            <Tab label="Transfer From" />
          </Tabs>
        </Box> */}

        {/* <TabPanel value={tabValue} index={0}> */}
        <Typography
          variant="body2"
          sx={{ color: colors.text, opacity: 0.7, mb: 2 }}
        >
          Transfer tokens to Safe: {safeName}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Amount"
            type="number"
            value={toAmount}
            onChange={handleToAmountChange}
            onKeyDown={handleAmountKeyDown}
            inputProps={{ min: 0, step: 'any' }}
            error={!!toAmountError}
            helperText={toAmountError}
            fullWidth
            sx={
              toAmountError
                ? createTextFieldErrorStyles(colors)
                : createTextFieldStyles(colors)
            }
          />
          <Typography
            variant="body2"
            sx={{
              color: colors.text,
              opacity: 0.7,
              fontSize: '0.875rem',
              mt: 1,
            }}
          >
            Tokens will be transferred from Treasury Safe (
            {formatAddress(treasurySafe)}) to {safeName}
          </Typography>
        </Box>
        {/* </TabPanel> */}

        {/* <TabPanel value={tabValue} index={1}>
          <Typography
            variant="body2"
            sx={{ color: colors.text, opacity: 0.7, mb: 2 }}
          >
            Transfer tokens from Safe: {safeName}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Amount"
              type="number"
              value={fromAmount}
              onChange={handleFromAmountChange}
              onKeyDown={handleAmountKeyDown}
              inputProps={{ min: 0, step: 'any' }}
              fullWidth
              sx={createTextFieldStyles(colors)}
            />
            <Typography
              variant="body2"
              sx={{
                color: colors.text,
                opacity: 0.7,
                fontSize: '0.875rem',
                mt: 1,
              }}
            >
              Tokens will be transferred from {safeName} to Treasury Safe (
              {formatAddress(treasurySafe)})
            </Typography>
          </Box>
        </TabPanel> */}
      </DialogContent>
      <DialogActions sx={{ p: 2, borderTop: `1px solid ${colors.primary}33` }}>
        <Button
          onClick={handleClose}
          sx={{
            color: colors.text,
            '&:hover': {
              backgroundColor: `${colors.text}22`,
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleTransfer}
          variant="contained"
          disabled={
            isTransferring ||
            isConfirmingTx ||
            (tabValue === 0 && !isTreasurySafeReady) ||
            (tabValue === 1 && !isOperationalSafeReady) ||
            (tabValue === 0 &&
              (!toAmount || parseFloat(toAmount) <= 0 || !!toAmountError)) ||
            (tabValue === 1 && (!fromAmount || parseFloat(fromAmount) <= 0))
          }
          sx={{
            backgroundColor: colors.yellow,
            color: colors.background,
            fontWeight: 600,
            '&:hover': {
              backgroundColor: colors.yellow,
              opacity: 0.9,
            },
            '&.Mui-disabled': {
              backgroundColor: `${colors.yellow}40`,
              color: `${colors.text}80`,
            },
          }}
        >
          {isTransferring || isConfirmingTx
            ? 'Processing...'
            : (tabValue === 0 && !isTreasurySafeReady) ||
              (tabValue === 1 && !isOperationalSafeReady)
            ? 'Initializing...'
            : 'Transfer'}
        </Button>
      </DialogActions>
      <TransactionCancelledModal
        open={showCancelledModal}
        onClose={() => {
          setShowCancelledModal(false)
          setCancelledMessage('')
        }}
        message={cancelledMessage}
      />
    </Dialog>
  )
}
